import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface DlpPolicyProps {
  apiUrl: string;
}

type DlpMode = 'off' | 'warn' | 'redact' | 'block';

interface Device {
  device_id: string;
  hostname?: string;
  os?: string;
  device_os?: string;
}

const MODES: DlpMode[] = ['off', 'warn', 'redact', 'block'];

const DlpPolicy: React.FC<DlpPolicyProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState<Device[]>([]);
  const [globalMode, setGlobalMode] = useState<DlpMode>('block');
  const [categories, setCategories] = useState<string[]>([]);
  const [deviceOverrides, setDeviceOverrides] = useState<Record<string, string>>({});
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState<{ hits: string[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const cfg = await fetch(`${apiUrl}/admin/bdr/dlp-config`).then((r) => r.json());
      setGlobalMode((cfg.global_mode as DlpMode) || 'block');
      setCategories(Array.isArray(cfg.categories) ? cfg.categories : []);
      setDeviceOverrides(cfg.device_overrides || {});
      const dev = await fetch(`${apiUrl}/admin/bdr/devices`).then((r) => r.json());
      setDevices(Array.isArray(dev.devices) ? dev.devices : []);
    } catch (err) {
      console.error('Failed to load DLP config:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const saveGlobalMode = async () => {
    setSaving(true);
    setSavedNotice(null);
    try {
      const r = await fetch(`${apiUrl}/admin/bdr/dlp-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ global_mode: globalMode }),
      });
      setSavedNotice(
        r.ok
          ? `Saved. Enrolled devices check in for this on their own schedule (~20s by default) — no restart needed.`
          : 'Save failed — see console.'
      );
      if (!r.ok) console.error('DLP config save failed:', await r.text().catch(() => r.statusText));
    } catch (err) {
      console.error(err);
      setSavedNotice('Save failed — see console.');
    } finally {
      setSaving(false);
    }
  };

  const saveDeviceMode = async (deviceId: string, mode: string) => {
    try {
      const r = await fetch(`${apiUrl}/admin/bdr/dlp-config/${deviceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      if (r.ok) setDeviceOverrides((prev) => ({ ...prev, [deviceId]: mode }));
    } catch (err) {
      console.error(err);
    }
  };

  const runTest = async () => {
    if (!testInput.trim()) return;
    try {
      const data = await fetch(`${apiUrl}/admin/bdr/dlp-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: testInput, mode: globalMode }),
      }).then((r) => r.json());
      setTestResult({ hits: Array.isArray(data.violations) ? data.violations : [] });
    } catch (err) {
      setTestResult({ hits: [] });
    }
  };

  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  if (loading) {
    return (
      <div className="page-content">
        <div className="ent-empty">{t('pages.dlpPolicy.loading')}</div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>{t('pages.dlpPolicy.dlpPolicy')}</h1>
          <p className="subtitle">{t('pages.dlpPolicy.dataLossPreventionForClaudeDesktop')}</p>
        </div>
      </div>

      {/* Global mode */}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">{t('pages.dlpPolicy.globalDefaultMode')}</div>
        <div className="ent-card-body">
          <div className="ent-toolbar" style={{ marginTop: 0 }}>
            {MODES.map((mode) => (
              <button
                key={mode}
                className={`ent-btn ${globalMode === mode ? 'primary' : ''}`}
                onClick={() => setGlobalMode(mode)}
              >
                {cap(mode)}
              </button>
            ))}
            <button className="ent-btn primary" onClick={saveGlobalMode} disabled={saving} style={{ marginLeft: 'auto' }}>
              {saving ? t('pages.dlpPolicy.saving') : t('pages.dlpPolicy.save')}
            </button>
          </div>
          <div className={`notice ${globalMode === 'block' ? 'error' : globalMode === 'off' ? 'info' : 'warn'}`} style={{ marginTop: 12 }}>
            {globalMode === 'off' && t('pages.dlpPolicy.modeOffHint')}
            {globalMode === 'warn' && t('pages.dlpPolicy.modeWarnHint')}
            {globalMode === 'redact' && t('pages.dlpPolicy.modeRedactHint')}
            {globalMode === 'block' && t('pages.dlpPolicy.modeBlockHint')}
          </div>
          {savedNotice && (
            <div className={`notice ${savedNotice.startsWith('Saved') ? 'success' : 'error'}`} style={{ marginTop: 8 }}>
              {savedNotice}
            </div>
          )}
        </div>
      </div>

      {/* Per-device overrides */}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">{t('pages.dlpPolicy.perDeviceOverrides')}</div>
        <div className="ent-card-body">
          {devices.length === 0 ? (
            <div className="ent-empty">{t('pages.dlpPolicy.noDevicesEnrolledYet')}</div>
          ) : (
            <div className="ent-table-wrap scroll">
              <table className="ent-table">
                <thead>
                  <tr>
                    <th>{t('pages.dlpPolicy.device')}</th>
                    <th style={{ width: 100 }}>{t('pages.dlpPolicy.os')}</th>
                    <th style={{ width: 220 }}>{t('pages.dlpPolicy.dlpMode')}</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device) => {
                    const override = deviceOverrides[device.device_id] || '';
                    return (
                      <tr key={device.device_id}>
                        <td>{device.hostname || device.device_id}</td>
                        <td>{device.os || device.device_os || '—'}</td>
                        <td>
                          <select
                            className="ent-input"
                            value={override}
                            onChange={(e) => saveDeviceMode(device.device_id, e.target.value || globalMode)}
                          >
                            <option value="">{t('pages.dlpPolicy.useGlobal', { v0: globalMode })}</option>
                            {MODES.map((mode) => (
                              <option key={mode} value={mode}>
                                {cap(mode)}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Test */}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">{t('pages.dlpPolicy.testDLPInspection')}</div>
        <div className="ent-card-body">
          <textarea
            className="ent-textarea"
            placeholder={t('pages.dlpPolicy.pasteSampleTextEGMy')}
            value={testInput}
            onChange={(e) => setTestInput(e.target.value)}
            rows={4}
          />
          <div className="ent-toolbar">
            <button className="ent-btn primary" onClick={runTest}>
              {t('pages.dlpPolicy.runTest')}
            </button>
          </div>
          {testResult && (
            <div className={`notice ${testResult.hits.length > 0 ? 'error' : 'success'}`}>
              {testResult.hits.length > 0
                ? `Detected: ${testResult.hits.join(', ')} — Action: ${globalMode.toUpperCase()}`
                : t('pages.dlpPolicy.noViolationsDetected')}
            </div>
          )}
        </div>
      </div>

      {/* Categories */}
      <div className="ent-card">
        <div className="ent-card-head">{t('pages.dlpPolicy.monitoredCategories')}</div>
        <div className="ent-card-body">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(categories.length > 0
              ? categories
              : ['SSN', 'EMAIL', 'CREDIT_CARD', 'PHONE', 'API_KEY', 'PASSWORD']
            ).map((cat) => (
              <span key={cat} className="badge badge-info">
                {cat}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DlpPolicy;
